// Base preference interfaces
export interface BasePreference {
  id: number;
  employee_id: number;
  reason?: string;
  priority: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
}

// Date preference interfaces
export interface DatePreference extends BasePreference {
  preference_date: string;
  preference_type: 'unavailable' | 'preferred';
  is_recurring: boolean;
  recurrence_pattern?: string;
  recurrence_interval?: number;
  recurrence_end_date?: string;
}

export interface CreateDatePreferenceData {
  employee_id: number;
  preference_date: string;
  preference_type: 'unavailable' | 'preferred';
  is_recurring?: boolean;
  recurrence_pattern?: string;
  recurrence_interval?: number;
  recurrence_end_date?: string;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface UpdateDatePreferenceData {
  preference_date?: string;
  preference_type?: 'unavailable' | 'preferred';
  is_recurring?: boolean;
  recurrence_pattern?: string;
  recurrence_interval?: number;
  recurrence_end_date?: string;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
}

// Day preference interfaces
export interface DayPreference extends BasePreference {
  day_of_week: number; // 0=Sunday, 6=Saturday
  preference_type: 'unavailable' | 'preferred' | 'neutral';
  time_restriction_start?: string;
  time_restriction_end?: string;
  week_of_month?: number;
  is_active: boolean;
}

export interface CreateDayPreferenceData {
  employee_id: number;
  day_of_week: number;
  preference_type: 'unavailable' | 'preferred' | 'neutral';
  time_restriction_start?: string;
  time_restriction_end?: string;
  week_of_month?: number;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface UpdateDayPreferenceData {
  day_of_week?: number;
  preference_type?: 'unavailable' | 'preferred' | 'neutral';
  time_restriction_start?: string;
  time_restriction_end?: string;
  week_of_month?: number;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
  is_active?: boolean;
}

// Time preference interfaces
export interface TimePreference extends BasePreference {
  preference_type: 'unavailable' | 'preferred' | 'neutral';
  start_time: string;
  end_time: string;
  applies_to_days: number[];
  max_daily_hours?: number;
  max_weekly_hours?: number;
  min_hours_between_shifts?: number;
  is_active: boolean;
}

export interface CreateTimePreferenceData {
  employee_id: number;
  preference_type: 'unavailable' | 'preferred' | 'neutral';
  start_time: string;
  end_time: string;
  applies_to_days?: number[];
  max_daily_hours?: number;
  max_weekly_hours?: number;
  min_hours_between_shifts?: number;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface UpdateTimePreferenceData {
  preference_type?: 'unavailable' | 'preferred' | 'neutral';
  start_time?: string;
  end_time?: string;
  applies_to_days?: number[];
  max_daily_hours?: number;
  max_weekly_hours?: number;
  min_hours_between_shifts?: number;
  reason?: string;
  priority?: 'low' | 'medium' | 'high';
  is_active?: boolean;
}

// Shift type preference interfaces
export interface ShiftTypePreference extends BasePreference {
  shift_type_id: number;
  preference_type: 'preferred' | 'acceptable' | 'avoid';
  experience_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  notes?: string;
  is_active: boolean;
}

export interface CreateShiftTypePreferenceData {
  employee_id: number;
  shift_type_id: number;
  preference_type: 'preferred' | 'acceptable' | 'avoid';
  experience_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  notes?: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface UpdateShiftTypePreferenceData {
  preference_type?: 'preferred' | 'acceptable' | 'avoid';
  experience_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  notes?: string;
  priority?: 'low' | 'medium' | 'high';
  is_active?: boolean;
}

// Preference override interfaces
export interface PreferenceOverride {
  id: number;
  schedule_id: number;
  employee_id: number;
  preference_type: string;
  preference_id?: number;
  override_reason: string;
  overridden_by: number;
  created_at: string;
}

export interface CreatePreferenceOverrideData {
  schedule_id: number;
  employee_id: number;
  preference_type: string;
  preference_id?: number;
  override_reason: string;
}

// Template interfaces
export interface PreferenceTemplate {
  id: number;
  name: string;
  description?: string;
  template_data: any;
  applies_to_role?: string;
  created_by?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePreferenceTemplateData {
  name: string;
  description?: string;
  template_data: any;
  applies_to_role?: string;
}

// Combined preference interfaces
export interface EmployeePreferences {
  date_preferences: DatePreference[];
  day_preferences: DayPreference[];
  time_preferences: TimePreference[];
  shift_type_preferences: ShiftTypePreference[];
}

export interface PreferenceConflict {
  has_conflicts: boolean;
  conflict_details: any[];
}

// UI-specific interfaces
export interface PreferenceFormData {
  type: 'date' | 'day' | 'time' | 'shift_type';
  data: CreateDatePreferenceData | CreateDayPreferenceData | CreateTimePreferenceData | CreateShiftTypePreferenceData;
}

export interface PreferenceSummary {
  total_preferences: number;
  date_preferences: number;
  day_preferences: number;
  time_preferences: number;
  shift_type_preferences: number;
  conflicts_count: number;
}

// Day of week constants
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

// Priority options
export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }
];

// Experience level options
export const EXPERIENCE_LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' }
];

// Preference type options for different contexts
export const DATE_PREFERENCE_TYPES = [
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'preferred', label: 'Preferred' }
];

export const DAY_PREFERENCE_TYPES = [
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'neutral', label: 'Neutral' }
];

export const TIME_PREFERENCE_TYPES = [
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'neutral', label: 'Neutral' }
];

export const SHIFT_TYPE_PREFERENCE_TYPES = [
  { value: 'preferred', label: 'Preferred' },
  { value: 'acceptable', label: 'Acceptable' },
  { value: 'avoid', label: 'Avoid' }
]; 